import {Radio, Segmented, Skeleton} from "antd";
import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useToast } from "react-native-toast-notifications";
import { useDispatch, useSelector } from "react-redux";
import {t} from "i18next";
import {fetchApi} from "../../../../../../services/HTTP";
import {getPromoCodes} from "../../../../../../redux/boxSlice";
import usePermissions from "../../../../../../services/Permissions";
import {analyzeYupSchema, checkRequired} from "../../../../../../services/stepperService";
import {
    buildMembershipTypesPayload,
    createSelectedMembershipsInEditMode,
    filterMembershipsBySelectedLocation, getVisibleMembershipTypes, PROMO_CODE_MT_TYPE, PROMO_CODE_OPTION,
    reArrangeMembershipTypes, TIERED_PROMO_CODE_MAX
} from "../../../../../../services/PromoCodeHelpers";
import VStack from "../../../../../common/Layout/VStack";
import FormInput from "../../../../../common/FormInput";
import HStack from "../../../../../common/Layout/HStack";
import CustomButtonV2 from "../../../../../common/CustomButtonV2";
import AdvanceSelect from "../../../../../common/AdvanceSelect";
import MultiSingleMenuWithSearch from "../../../../../common/MultiSingleMenuWithSearch";
import {Colors} from "../../../../../../styles/Colors";
import AppText from "../../../../../common/AppText";
import InfoTooltip from "../../../../../common/InfoTooltip";
import {GlobalConsts} from "../../../../../../styles/GlobalConsts";
import {dbDateFormat, isMobile} from "../../../../../../services/Helpers"
import SharedTimepicker from "../../../../../common/SharedTimepicker";
import {InternalPromoRoleOptions, Roles} from "../../../../../../Configs/DatabaseConsts";
import CustomCheckbox from "../../../../../common/CustomCheckbox";


const LIMIT_PER_USER_MAX = 10;

const PromoCode = (props) => {
    const { target, errors, values, setFieldValue, touched, validationSchema } = props;
    const box = useSelector((state) => state.box?.box);
    const [membershipTypes, setMembershipTypes] = useState(null);
    const [services, setServices] = useState(null);
    const [cycles, setCycles] = useState(values.recurring_cycles > 0 ? values.recurring_cycles : 1);

    const dispatch = useDispatch();
    const toast = useToast();
    const { has } = usePermissions();

    const isEditMode = useMemo(() => !!target, [target]);
    const validationObject = useMemo(() => analyzeYupSchema(validationSchema), [validationSchema],);
    const locations = useMemo(() => box?.locations_box ?? [], [box]);
    const allowRecurring = useMemo(() => has("recurringPaymentActions") && has("allowRecurringPromoCode"), [has],);
    const allowHugim = useMemo(() => has("hugim"), [has]);
    const allowTieredPromoCodes = useMemo(() => !allowHugim && has("tieredPromoCodes"), [allowHugim, has],);
    const tieredActive = useMemo(() => values.tiered_promo_codes !== null, [values.tiered_promo_codes],);

    const membershipTypesAllLocations = useMemo(() => reArrangeMembershipTypes({membershipTypes, services, allowHugim, allowPlan: has('membershipPlans'), internal: !!values?.internal}), [membershipTypes, services, allowHugim, values?.internal]);
    const membershipTypesWithRecurring = useMemo(() => filterMembershipsBySelectedLocation(membershipTypesAllLocations, values.location_id, locations,), [membershipTypesAllLocations, values.location_id, locations],);
    const visibleMembershipTypes = useMemo(() => getVisibleMembershipTypes({membershipTypesWithRecurring, recurringCycles: values.recurring_cycles, allowHugim, tieredPromoCodesActive: tieredActive}), [membershipTypesWithRecurring, values.recurring_cycles, allowHugim, tieredActive]);
    const limitPerUserOptions = useMemo(() => [{ value: PROMO_CODE_OPTION.UNLIMITED, label: t("unlimited") }, ...Array.from({ length: LIMIT_PER_USER_MAX }, (_, i) => ({value: i + 1, label: String(i + 1),}))], []);
    const showLocationField = useMemo(() => !!target && locations.length > 1, [target, locations],);

    const locationOptions = useMemo(() => {
        const mapped = locations.map((l) => ({ value: l.id, label: l.location }));
        if (locations.length <= 1) return mapped;
        return [{ value: null, label: t("promoNoLocation") }, ...mapped];
    }, [locations]);

    const selectedIdsByType = useMemo(() => {
        const flat = values.membership_types ?? [];
        const out = {};
        Object.entries(visibleMembershipTypes).forEach(([type, items]) => {
            if (type === PROMO_CODE_MT_TYPE.SERVICE) {
                out[type] = items.filter((s) => s.membership_type_ids?.some((id) => flat.includes(id))).map((s) => s.id);
            } else {
                const ids = items.map((m) => m.id);
                out[type] = flat.filter((id) => ids.includes(id));
            }
        });
        return out;
    }, [visibleMembershipTypes, values.membership_types]);

    useEffect(() => {
        getMembershipTypesAllLocations();
    }, []);

    useEffect(() => {
        if (!services || !membershipTypes) return;
        const useEditHydration = isEditMode && values.location_id === target.location_id;
        const selectedByType = useEditHydration ? createSelectedMembershipsInEditMode({ target, services, allMembershipTypes: membershipTypes, allowHugim }) : Object.fromEntries(Object.entries(visibleMembershipTypes).map(([type, items]) => [type, items.map((m) => m.id)]));
        setFieldValue("membership_types", buildMembershipTypesPayload(selectedByType, services));
    }, [services, membershipTypes, values.location_id, values.recurring_cycles === null, tieredActive]);

    const getMembershipTypesAllLocations = async () => {
        try {
            await dispatch(getPromoCodes());
            const mt = await fetchApi("promoCode/getMembershipTypes", "GET", null, false, true,);
            setMembershipTypes(mt);
            const svc = await fetchApi("promoCode/getServices", "GET", null, false, true,);
            setServices(svc);
        } catch (error) {
            toast.show(t("somethingWentWrongPleaseTryAgain"), {type: "danger", duration: 4000,});
        }
    };

    const onMembershipSelectChange = (type, ids) => {
        const items = visibleMembershipTypes[type] ?? [];
        const next = ids ?? [];
        const isService = type === PROMO_CODE_MT_TYPE.SERVICE;
        const oldFlatForType = isService ? items.flatMap((s) => s.membership_type_ids ?? []) : items.map((m) => m.id);
        const newFlatForType = isService ? next.flatMap((sid) => items.find((s) => s.id === sid)?.membership_type_ids ?? []) : next;
        const others = (values.membership_types ?? []).filter((id) => !oldFlatForType.includes(id));
        setFieldValue("membership_types", [...others, ...newFlatForType]);
    };

    const selectRecurringOption = (nextCycles) => {
        setFieldValue("recurring_cycles", nextCycles);
        if (tieredActive) {
            setFieldValue("tiered_promo_codes", null);
        }
    };

    const onCyclesChange = (n) => {
        const safe = n && n > 0 ? n : 1;
        setCycles(safe);
        if (values.recurring_cycles > 0) {
            setFieldValue("recurring_cycles", safe);
        }
    };

    const selectTieredPromoCodes = () => {
        setFieldValue("discount_amount", null);
        setFieldValue("recurring_cycles", 1);
        if (!tieredActive) {
            setFieldValue("tiered_promo_codes", [{ discount_amount: null, cycle: 1 }]);
        }
    };

    const addTieredPromoCode = () => {
        const current = values.tiered_promo_codes ?? [];
        if (current.length >= TIERED_PROMO_CODE_MAX) return;
        setFieldValue("tiered_promo_codes", [...current, { discount_amount: null, cycle: current.length + 1 },]);
    };

    const removeTieredPromoCode = (index) => {
        const next = (values.tiered_promo_codes ?? []).filter((_, i) => i !== index)
            .map((tpc, i) => ({ ...tpc, cycle: i + 1 }));
        setFieldValue("tiered_promo_codes", next.length > 0 ? next : null);
    };

    const setTieredPromoCodeDiscount = (index, discount) => {
        const next = [...(values.tiered_promo_codes ?? [])];
        next[index] = { ...next[index], discount_amount: discount };
        setFieldValue("tiered_promo_codes", next);
    };

    const changeRecurringRadio = (radio) => {
        if (radio === "all") selectRecurringOption(0);
        else if (radio === "first-n") selectRecurringOption(cycles);
        else selectTieredPromoCodes();
    }

    const updateStaffArray = (role) => {
        let newArr = [...(values.staff ?? [])];
        if(newArr.includes(role)) {
            newArr = newArr.filter(r => r !== role)
        } else {
            newArr.push(role)
        }
        setFieldValue('staff', newArr)
    }

    return (
        <VStack space={"0.5rem"}>
            <VStack>
                <FormInput
                    width={"100%"}
                    label={`${t("code")}${checkRequired("name", validationObject)}`}
                    placeholder={t("code")}
                    maxLength={20}
                    value={values.name}
                    onChangeCallback={(v) => setFieldValue("name", v)}
                    error={touched.name && errors.name ? errors.name : null}
                />

                <FormInput
                    width={"150px"}
                    label={t("discount")}
                    numericOnly
                    inputType={"float"}
                    min={1}
                    max={100}
                    value={values.discount_amount}
                    onChangeCallback={(v) => setFieldValue("discount_amount", v)}
                    disabled={tieredActive}
                    formatAddon={"%"}
                    controls={true}
                    error={touched.discount_amount && errors.discount_amount ? errors.discount_amount : null}
                />

                <FormInput label={t("expiringDate")} error={touched.expiration_date && errors.expiration_date ? errors.expiration_date : null}>
                    <Radio.Group value={values.expiration_date ? 'limited' : 'unlimited'} onChange={(e) => setFieldValue("expiration_date", e.target.value === 'unlimited' ? null : moment().add(1, 'days').format(dbDateFormat))}>
                        <VStack space={'0.1rem'}>
                            <HStack alignItems={'center'}>
                                <Radio value="unlimited">
                                    <AppText>{t("no-end-date")}</AppText>
                                </Radio>
                            </HStack>
                            <HStack width={'max-content'} alignItems={'center'} h={'38px'}>
                                <Radio value="limited">
                                    <HStack space={'1rem'} alignItems={'center'}>
                                    <AppText>{t("select-end-date")}</AppText>
                                    {values.expiration_date !== null && (
                                        <SharedTimepicker
                                            type={'date'}
                                            value={moment(values.expiration_date, dbDateFormat)}
                                            onChange={(expiration_date) => setFieldValue('expiration_date', expiration_date ? expiration_date.format(dbDateFormat) : null)}
                                            inputStyle={[{width: 'fit-content'}]}
                                        />
                                    )}
                                    </HStack>
                                </Radio>
                            </HStack>
                        </VStack>
                    </Radio.Group>
                </FormInput>

                <FormInput width={"100%"} label={t("limitPerUser")}>
                    <AdvanceSelect
                        options={limitPerUserOptions}
                        initValue={values.limit_quantity === null ? PROMO_CODE_OPTION.UNLIMITED : values.limit_quantity}
                        onChange={(val) => setFieldValue("limit_quantity", val?.value === PROMO_CODE_OPTION.UNLIMITED ? null : val?.value ?? null,)}
                        hideCreateBtn
                        dontSort
                    />
                </FormInput>

                {showLocationField && (
                    <FormInput width={"100%"} label={t("location")}>
                        <AdvanceSelect
                            options={locationOptions}
                            initValue={values.location_id}
                            onChange={(val) => setFieldValue("location_id", val?.value ?? null)}
                            hideCreateBtn
                            dontSort
                        />
                    </FormInput>
                )}
            </VStack>

            {allowRecurring && !isEditMode && (
                <div style={{width: 'auto', marginBottom: '0.5rem'}}>
                    <Segmented
                        value={values.recurring_cycles === null ? 'regular' : 'recurring'}
                        options={[
                            { label: t("regular"), value: 'regular' },
                            { label: t("recurring"), value: 'recurring' },
                        ]}
                        onChange={(val) => selectRecurringOption(val === 'regular' ? null : 0)}
                    />
                </div>
            )}

            {values.recurring_cycles !== null && (
                <FormInput width={"100%"} label={t("recurringRadioHeader")}>
                    <Radio.Group value={tieredActive ? "tiered" : (values.recurring_cycles === 0 ? "all" : "first-n")} onChange={(e) => changeRecurringRadio(e.target.value)}>
                        <VStack space={"0.5rem"}>
                            <Radio value={"all"}>
                                <HStack alignItems={"center"}>
                                    <AppText>{t("allRecurring")}</AppText>
                                    <InfoTooltip title={t("allRecurringTooltip")} />
                                </HStack>
                            </Radio>
                            <Radio value={"first-n"}>
                                <HStack space={"0.5rem"} alignItems={"center"}>
                                    <FormInput numericOnly min={1} max={12} width={"70px"} value={cycles} onChangeCallback={onCyclesChange} containerProps={{ mb: 0 }} />
                                    <HStack>
                                        <AppText>{t("numFirstCycles")}</AppText>
                                        <InfoTooltip title={t("numFirstCyclesTooltip")} />
                                    </HStack>
                                </HStack>
                            </Radio>
                            {allowTieredPromoCodes && (
                                <Radio value={"tiered"}>
                                    <HStack alignItems={"center"}>
                                        <AppText>{t("tieredPromoCode")}</AppText>
                                        <InfoTooltip title={t("tieredPromoCodesTooltip")} />
                                    </HStack>
                                </Radio>
                            )}
                            {tieredActive && (
                                <VStack space={"1rem"}>
                                    {values.tiered_promo_codes.map((tpc, i) => (
                                        <HStack key={`tiered_promo_codes-${i}`} space={"0.5rem"} alignItems={'center'}>
                                            <FormInput numericOnly
                                                       min={1}
                                                       max={100}
                                                       width={"95px"}
                                                       value={tpc.discount_amount}
                                                       formatAddon={"%"}
                                                       onChangeCallback={(v) => setTieredPromoCodeDiscount(i, v)}
                                                       containerProps={{ mb: 0 }}
                                                       error={touched.tiered_promo_codes?.[i]?.discount_amount && errors.tiered_promo_codes?.[i]?.discount_amount}
                                            />
                                            <AppText>{`${t("tieredPromoCodeCycle")} ${i + 1}`}</AppText>
                                            {i > 0 && i === values.tiered_promo_codes.length - 1 && <CustomButtonV2 tooltipText={t("removeTieredCycle")} type={'link'} iconName={'fa-trash-can'} iconProps={{ iconSize: GlobalConsts.iconSize.XS, iconColor: Colors.error, iconHoverColor: Colors.error }} onClick={() => removeTieredPromoCode(i)}/>}
                                        </HStack>
                                    ))}
                                    {values.tiered_promo_codes.length < TIERED_PROMO_CODE_MAX && <CustomButtonV2 onClick={addTieredPromoCode} text={`+ ${t("addNewTieredCycle")}`} applyHoverOnText={true} type={"link"} wrapperStyle={{width: 'fit-content'}} iconProps={{iconColor: Colors.newDesign.mainBlue, iconHoverColor: Colors.newDesign.mainBlueHover}}/>}
                                </VStack>
                            )}
                        </VStack>
                    </Radio.Group>
                </FormInput>
            )}

            {(!services || !membershipTypes) &&
                <VStack space={'5px'}>
                    <Skeleton active paragraph={{rows: 1, width: '100%'}} title={false}/>
                    <Skeleton active paragraph={{rows: 1, width: '100%'}} title={false}/>
                    <Skeleton active paragraph={{rows: 1, width: '100%'}} title={false}/>
                    <Skeleton active paragraph={{rows: 1, width: '100%'}} title={false}/>
                </VStack>
            }

            <VStack>
                {services && membershipTypes && Object.entries(visibleMembershipTypes).map(([type, memberships]) => (
                    <FormInput key={type} width={"100%"} label={`${t(`promoCodeSelectLabel-${type}`)}${checkRequired("membership_types", validationObject,)}`}>
                        <MultiSingleMenuWithSearch
                            searchable
                            allowClear
                            labelProp={"name"}
                            valueProp={"id"}
                            options={memberships}
                            value={selectedIdsByType[type] ?? []}
                            onChange={(ids) => onMembershipSelectChange(type, ids)}
                            disabled={memberships.length === 0}
                            placeholder={t("search")}
                            dropdownRender={(menu) => (
                                <>
                                    {menu}
                                    {(selectedIdsByType[type]?.length ?? 0) !== memberships.length && (
                                        <CustomButtonV2
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                onMembershipSelectChange(type, memberships.map((m) => m.id),);
                                            }}
                                            text={t("selectAll")}
                                            applyHoverOnText={true}
                                            type={"link"}
                                            iconProps={{iconColor: Colors.newDesign.mainBlue, iconHoverColor: Colors.newDesign.mainBlueHover,}}
                                            wrapperStyle={{ marginTop: "8px" }}
                                        />
                                    )}
                                </>
                            )}
                        />
                    </FormInput>
                ))}
            </VStack>

            {!!values?.internal && values.staff &&
                <FormInput label={t("internalPromoStaffLabel")} infoTooltip={t("internalPromoStaffTooltip")}>
                    <VStack space={'0.4rem'}>{InternalPromoRoleOptions.map(role =>
                        <CustomCheckbox text={t(role)}
                                        isChecked={(values.staff ?? []).includes(role)}
                                        onChange={() => updateStaffArray(role)}
                                        disabled={role === Roles.BOXADMIN}
                                        value={role}
                        />)}
                    </VStack>
                </FormInput>
            }
        </VStack>
    );
};

export default PromoCode;
